import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';
import SEO from '../components/SEO';
import {
  ArrowRight, Globe, Mic, Target, Mail, Users, Shield,
  Check, TrendingUp, Clock, Zap
} from 'lucide-react';

const APP_SCREENSHOTS = {
  dashboard: 'https://customer-assets.emergentagent.com/job_51ed1aac-5a3e-49a3-82ed-e8a2d190f4c1/artifacts/gfn0k5v8_Perfekt.png',
  discovery: 'https://customer-assets.emergentagent.com/job_51ed1aac-5a3e-49a3-82ed-e8a2d190f4c1/artifacts/j3u6epqb_Example%20richtig2.png',
  detail: 'https://customer-assets.emergentagent.com/job_51ed1aac-5a3e-49a3-82ed-e8a2d190f4c1/artifacts/mn6mabdb_Example%20richtig1.png',
  charts: 'https://customer-assets.emergentagent.com/job_51ed1aac-5a3e-49a3-82ed-e8a2d190f4c1/artifacts/z3fctnvq_Passt1.png',
};

const FEATURES = [
  { icon: Globe, title: 'Website-Analyse', desc: 'Automatische Erkennung Ihres Geschäftsmodells, Ihrer Produkte und Zielgruppen durch KI-gestützte Website-Analyse.' },
  { icon: Mic, title: 'Voice-to-ICP', desc: 'Sprechen Sie frei über Ihren idealen Kunden. Unsere KI erstellt daraus ein strukturiertes Ideal Customer Profile.' },
  { icon: Target, title: 'KI-Discovery', desc: 'Automatische Identifikation passender B2B-Unternehmen mit Matching-Score, Fit- und Bedarfsanalyse.' },
  { icon: Mail, title: 'Outreach-Vorbereitung', desc: 'KI-generierte E-Mail-Entwürfe und Call-Skripte, personalisiert auf jeden potenziellen Kunden.' },
  { icon: Users, title: 'Team-Verwaltung', desc: 'Laden Sie Ihr Vertriebsteam ein. Gemeinsam Leads entdecken und Outreach koordinieren.' },
  { icon: Shield, title: 'DSGVO-konform', desc: 'Opt-Out-Management, Audit-Logging und Workspace-basierte Zugriffskontrolle für volle Compliance.' },
];

const STEPS = [
  { num: '01', title: 'Registrieren & URL eingeben', desc: 'Erstellen Sie Ihr Konto und geben Sie Ihre Unternehmens-Website ein. Die KI analysiert automatisch Ihr Angebot.' },
  { num: '02', title: 'ICP definieren', desc: 'Beschreiben Sie per Sprache oder Text Ihren idealen Kunden. Die KI erstellt ein strukturiertes Suchprofil.' },
  { num: '03', title: 'Kunden entdecken', desc: 'Starten Sie die KI-Recherche. Erhalten Sie qualifizierte B2B-Leads mit Kontaktdaten und Matching-Score.' },
  { num: '04', title: 'Kontakt aufnehmen', desc: 'Nutzen Sie KI-generierte E-Mails und Call-Skripte für eine personalisierte Erstansprache.' },
];

const FAQ_ITEMS = [
  { q: 'Wie funktioniert die Website-Analyse?', a: 'Unsere KI crawlt Ihre Website, extrahiert Informationen zu Produkten, Services, Branche und Wertversprechen und erstellt daraus ein strukturiertes Unternehmensprofil. Dieser Prozess dauert nur wenige Sekunden.' },
  { q: 'Muss ich ein Mikrofon verwenden?', a: 'Nein, Sie können Ihren idealen Kunden auch per Texteingabe beschreiben. Die Spracheingabe ist optional und bietet eine schnellere, natuerlichere Alternative.' },
  { q: 'Wie genau sind die Ergebnisse?', a: 'Unsere KI liefert einen Matching-Score (0-100) basierend auf Passung, Bedarf und Erreichbarkeit. Jeder Lead wird individuell bewertet, damit Sie sich auf die vielversprechendsten Kontakte konzentrieren können.' },
  { q: 'Kann ich mein Team einladen?', a: 'Ja, als Owner können Sie Teammitglieder per Einladungslink hinzufuegen. Die maximale Anzahl hängt von Ihrem gewaehlten Plan ab.' },
  { q: 'Welche Zahlungsmethoden werden akzeptiert?', a: 'Wir akzeptieren Kreditkarten (Visa, Mastercard, Amex) über Stripe sowie Zahlung auf Rechnung. Beides sowohl monatlich als auch jaehrlich moeglich.' },
  { q: 'Gibt es eine Kündigungsfrist?', a: 'Nein, Sie können jederzeit zum Monatsende kündigen. Keine versteckten Kosten, keine langfristigen Vertraege.' },
  { q: 'Kann ich LaizyLeads kostenlos testen?', a: 'Nach Absprache bieten wir die Moeglichkeit eines kostenlosen Tests an. Kontaktieren Sie uns fuer ein unverbindliches Beratungsgespraech — gemeinsam finden wir heraus, ob LaizyLeads zu Ihrem Vertrieb passt.' },
  { q: 'Was unterscheidet LaizyLeads von anderen Leadgeneratoren?', a: 'Wir setzen auf Qualitaet statt Quantitaet. Unsere KI findet Kunden, die perfekt zu Ihrem Angebot passen — Kunden, die Sie bisher nicht auf dem Schirm hatten. Zahlreiche Unternehmen generieren bereits Umsatz mit unseren Leads.' },
  { q: 'Wie wird der Datenschutz gewährleistet?', a: 'Alle Daten werden Workspace-basiert getrennt (Multi-Tenancy). Wir bieten Opt-Out-Management, Audit-Logging und halten uns an die DSGVO-Richtlinien.' },
];

const FALLBACK_PLANS = [
  { id: 'basic', name: 'Basic', monthly_eur: 69.99, yearly_eur: 769.89, max_users: 1, max_leads_per_month: 100, addon_leads: 100, addon_price_eur: 39.99, badge_text: '' },
  { id: 'pro', name: 'Pro', monthly_eur: 179, yearly_eur: 1969, max_users: 3, max_leads_per_month: 300, addon_leads: 200, addon_price_eur: 69.99, badge_text: 'Beliebt' },
  { id: 'premium', name: 'Premium', monthly_eur: 399, yearly_eur: 4389, max_users: 10, max_leads_per_month: 1000, addon_leads: 500, addon_price_eur: 159.99, badge_text: '' },
];

/* Scroll animation hook */
function useScrollReveal() {
  const observerRef = useRef(null);
  const initObserver = useCallback(() => {
    if (observerRef.current) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('scroll-visible');
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('[data-scroll]').forEach((el) => observerRef.current?.observe(el));
  }, []);

  useEffect(() => {
    const t = setTimeout(initObserver, 100);
    return () => { clearTimeout(t); observerRef.current?.disconnect(); };
  }, [initObserver]);
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [billingInterval, setBillingInterval] = useState('monthly');

  useScrollReveal();

  useEffect(() => {
    fetch('/api/plans/config')
      .then(r => r.json())
      .then(d => { if (d.plans?.length) setPlans(d.plans); else setPlans(FALLBACK_PLANS); })
      .catch(() => setPlans(FALLBACK_PLANS));
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="landing-page">
      <SEO
        title="LaizyLeads – B2B Leads generieren mit KI | Hochwertige Leadgenerierung"
        description="Hochwertige B2B Leads generieren statt Massenkontakte. KI-gestützter Leadgenerator findet gezielt passende Unternehmen, qualifizierte Leads und neue B2B-Kunden – datenbasiert und automatisiert."
        path="/"
        keywords="B2B Leads generieren, Leadgenerierung B2B, hochwertige Leads, Leadgenerator, neue Kunden gewinnen B2B, qualifizierte Leads, automatisierte Leadgenerierung"
      />
      <MarketingNav />

      {/* ═══ HERO ═══ */}
      <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-24 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-gray-50" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-emerald-100/40 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-6 bg-emerald-100 text-emerald-700 border-emerald-200 px-3.5 py-1.5 text-sm font-medium" data-testid="hero-badge">
                <Zap className="w-3.5 h-3.5 mr-1.5" /> KI-gestützte B2B-Leadgenerierung
              </Badge>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6 text-gray-900" data-testid="hero-title">
                Finden Sie neue Kunden.
                <span className="block text-emerald-600">Automatisch.</span>
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed mb-4 max-w-xl" data-testid="hero-subtitle">
                Sparen Sie <strong className="text-gray-900">80% der Recherchezeit</strong> und steigern Sie Ihre Kundenakquise um ein Vielfaches. Unser performance-optimierter Leadgenerator findet passende B2B-Unternehmen <strong className="text-gray-900">deutschlandweit oder regional</strong> – mit Kontaktdaten und personalisierter Ansprache.
              </p>

              <div className="flex flex-wrap gap-4 mb-8 text-sm text-gray-500">
                <div className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-emerald-600" />
                  <span>Stunden statt Tage</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                  <span>Mehr Abschlüsse</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Target className="w-4 h-4 text-emerald-600" />
                  <span>Qualifizierte Leads</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white h-13 px-8 text-base shadow-xl shadow-emerald-600/20 transition-all hover:-translate-y-0.5"
                  onClick={() => navigate('/register')} data-testid="hero-register-btn">
                  Jetzt registrieren <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button size="lg" variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 h-13 px-8 text-base"
                  onClick={() => scrollTo('how-it-works')} data-testid="hero-demo-btn">
                  So funktioniert's
                </Button>
              </div>
            </div>

            {/* App Preview */}
            <div className="relative" data-scroll="fade-up">
              <div className="rounded-2xl overflow-hidden shadow-2xl shadow-gray-900/10 border border-gray-200">
                <img src={APP_SCREENSHOTS.dashboard} alt="LaizyLeads Dashboard" className="w-full" loading="lazy" />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 rounded-xl overflow-hidden shadow-xl border border-gray-200 hidden lg:block">
                <img src={APP_SCREENSHOTS.detail} alt="Lead Detail" className="w-full" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SOCIAL PROOF / STATS ═══ */}
      <section id="vorteile" className="py-16 bg-gray-50 border-y border-gray-200" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-12" data-scroll="fade-up">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-3">
              Warum Vertriebsteams LaizyLeads nutzen
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Die manuelle Kundenrecherche kostet Sie wertvolle Stunden. Unsere KI erledigt das in Minuten.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-scroll="fade-up" data-scroll-delay={1}>
            <div className="text-center p-8 rounded-2xl bg-white border border-gray-200 shadow-sm" data-testid="stat-time">
              <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-emerald-600" />
              </div>
              <p className="text-4xl font-bold text-emerald-600 mb-2">80%</p>
              <p className="text-sm font-semibold text-gray-900 mb-1">weniger Recherchezeit</p>
              <p className="text-sm text-gray-500">Von Tagen auf Minuten. Die KI durchsucht das Web und liefert qualifizierte Leads direkt auf Ihr Dashboard.</p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-white border border-gray-200 shadow-sm" data-testid="stat-leads">
              <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Target className="w-7 h-7 text-emerald-600" />
              </div>
              <p className="text-4xl font-bold text-emerald-600 mb-2">10x</p>
              <p className="text-sm font-semibold text-gray-900 mb-1">mehr qualifizierte Leads</p>
              <p className="text-sm text-gray-500">Finden Sie passende Unternehmen in jeder Branche und Region — mit KI-bewertetem Matching-Score.</p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-white border border-gray-200 shadow-sm" data-testid="stat-revenue">
              <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-7 h-7 text-emerald-600" />
              </div>
              <p className="text-4xl font-bold text-emerald-600 mb-2">+35%</p>
              <p className="text-sm font-semibold text-gray-900 mb-1">höhere Abschlussrate</p>
              <p className="text-sm text-gray-500">Personalisierte Ansprache auf Basis von KI-Analysen steigert Ihre Erfolgsquote messbar.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ APP SHOWCASE ═══ */}
      <section className="py-20 sm:py-28 bg-white" data-testid="showcase-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-16" data-scroll="fade-up">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-emerald-200">Live-Einblick</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">
              So sieht Ihre neue Kundenakquise aus
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Dashboard, Lead-Recherche und detaillierte Firmenprofile — alles auf einen Blick.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Discovery screenshot */}
            <div className="group" data-scroll="fade-up" data-scroll-delay={0}>
              <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-lg transition-all duration-300 group-hover:shadow-xl group-hover:-translate-y-1">
                <img src={APP_SCREENSHOTS.discovery} alt="Lead-Recherche Ansicht" className="w-full" loading="lazy" />
              </div>
              <div className="mt-4 px-1">
                <h3 className="font-semibold text-gray-900 mb-1">Lead-Recherche</h3>
                <p className="text-sm text-gray-500">Alle gefundenen Unternehmen auf einen Blick — mit Branche, Standort, Telefon und KI-Matching-Score.</p>
              </div>
            </div>

            {/* Detail screenshot */}
            <div className="group" data-scroll="fade-up" data-scroll-delay={1}>
              <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-lg transition-all duration-300 group-hover:shadow-xl group-hover:-translate-y-1">
                <img src={APP_SCREENSHOTS.detail} alt="Lead-Detailansicht" className="w-full" loading="lazy" />
              </div>
              <div className="mt-4 px-1">
                <h3 className="font-semibold text-gray-900 mb-1">Lead-Detailprofil</h3>
                <p className="text-sm text-gray-500">Passung, Bedarf und Erreichbarkeit einzeln bewertet. Plus Kontaktdaten und KI-generierte Outreach-Vorlagen.</p>
              </div>
            </div>

            {/* Dashboard charts screenshot - full width */}
            <div className="group lg:col-span-2" data-scroll="fade-up" data-scroll-delay={2}>
              <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-lg transition-all duration-300 group-hover:shadow-xl group-hover:-translate-y-1">
                <img src={APP_SCREENSHOTS.charts} alt="Dashboard Statistiken" className="w-full" loading="lazy" />
              </div>
              <div className="mt-4 px-1">
                <h3 className="font-semibold text-gray-900 mb-1">Statistiken & Deutschlandkarte</h3>
                <p className="text-sm text-gray-500">Behalten Sie den überblick: Leads nach Branche, Status-Verteilung und regionale Aktivitaet auf einer Karte.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section id="features" className="py-20 sm:py-28 bg-gray-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-16" data-scroll="fade-up">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-emerald-200">Funktionen</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">
              Alles für Ihre B2B-Kundenakquise
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Von der automatischen Analyse bis zur personalisierten Ansprache — eine Plattform für Ihren gesamten Vertriebsprozess.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} data-scroll="fade-up" data-scroll-delay={i}
                  className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-200 hover:shadow-md transition-all duration-300"
                  data-testid={`feature-card-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center mb-4 group-hover:bg-emerald-200 transition-colors">
                    <Icon className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-gray-900">{f.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how-it-works" className="py-20 sm:py-28 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-16" data-scroll="fade-up">
            <Badge className="mb-4 bg-gray-200 text-gray-700 border-gray-300">4 Schritte</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">
              In 4 Schritten zu neuen Kunden
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Von der Registrierung zum ersten Kundenkontakt in wenigen Minuten.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={i} className="relative" data-scroll="fade-up" data-scroll-delay={i} data-testid={`step-card-${i}`}>
                {i < STEPS.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-[calc(100%+0.25rem)] w-[calc(100%-3rem)] h-px bg-gradient-to-r from-emerald-300 to-transparent" />
                )}
                <div className="p-6 rounded-2xl border border-gray-200 bg-gray-50 h-full hover:border-emerald-200 transition-colors">
                  <span className="text-3xl font-bold text-emerald-600/30 block mb-3">{s.num}</span>
                  <h3 className="text-base font-semibold mb-2 text-gray-900">{s.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12" data-scroll="fade-up" data-scroll-delay={4}>
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white h-12 px-8 text-base shadow-lg shadow-emerald-600/20"
              onClick={() => navigate('/register')} data-testid="steps-register-btn">
              Jetzt registrieren <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section id="pricing" className="py-20 sm:py-28 bg-gray-50" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-12" data-scroll="fade-up">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-emerald-200">Preise</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">
              Transparente Preise, keine versteckten Kosten
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Wählen Sie den Plan, der zu Ihrem Team passt. Jederzeit kündbar.
            </p>
          </div>

          <div className="flex items-center justify-center gap-4 mb-10" data-scroll="fade-up" data-scroll-delay={1}>
            <span className={`text-sm font-semibold transition-colors ${billingInterval === 'monthly' ? 'text-gray-900' : 'text-gray-400'}`}>Monatlich</span>
            <button onClick={() => setBillingInterval(i => i === 'monthly' ? 'yearly' : 'monthly')} data-testid="landing-interval-toggle"
              className={`relative inline-flex h-8 w-[56px] items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${billingInterval === 'yearly' ? 'bg-emerald-600' : 'bg-gray-300'}`}
              role="switch" aria-checked={billingInterval === 'yearly'}>
              <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform duration-300 ${billingInterval === 'yearly' ? 'translate-x-7' : 'translate-x-1'}`} />
            </button>
            <span className={`text-sm font-semibold transition-colors ${billingInterval === 'yearly' ? 'text-gray-900' : 'text-gray-400'}`}>Jährlich</span>
            {billingInterval === 'yearly' && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs ml-1 animate-fade-in-up">Spare bis zu 10%</Badge>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan, idx) => {
              const isPopular = plan.id === 'pro';
              const totalMonthly = plan.monthly_eur;
              const totalYearly = plan.yearly_eur;
              const perMonth = billingInterval === 'monthly' ? totalMonthly : (totalYearly / 12);
              const perUser = perMonth / plan.max_users;
              const yearlySaving = (totalMonthly * 12) - totalYearly;

              return (
                <div key={plan.id}
                  className={`relative rounded-2xl animate-fade-in-up ${isPopular ? 'ring-2 ring-emerald-500 shadow-xl shadow-emerald-600/10' : 'border border-gray-200 shadow-sm'}`}
                  style={{ animationDelay: `${idx * 100}ms`, animationFillMode: 'both' }}
                  data-testid={`landing-plan-${plan.id}`}>
                  {isPopular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                      <Badge className="bg-emerald-600 text-white border-0 shadow-lg px-4">{plan.badge_text}</Badge>
                    </div>
                  )}
                  <div className="h-full rounded-2xl p-7 bg-white">
                    <div className="mb-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-1">{plan.name}</h3>
                      <p className="text-sm text-gray-500">
                        <Users className="w-3.5 h-3.5 inline mr-1" />
                        {plan.max_users === 1 ? '1 Nutzer' : `Bis zu ${plan.max_users} Nutzer`}
                      </p>
                    </div>
                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-gray-900">{perUser.toFixed(2).replace('.', ',')}&euro;</span>
                        <span className="text-gray-400 text-sm">/Nutzer/Monat</span>
                      </div>
                      {plan.max_users > 1 && (
                        <p className="text-xs text-gray-400 mt-1">Gesamt: {perMonth.toFixed(2).replace('.', ',')}&euro;/Monat</p>
                      )}
                      {billingInterval === 'yearly' && yearlySaving > 0 && (
                        <p className="text-xs font-medium text-emerald-600 mt-1">{yearlySaving.toFixed(0)}&euro; pro Jahr sparen</p>
                      )}
                    </div>
                    <ul className="space-y-3 mb-8">
                      {[
                        `${plan.max_leads_per_month || 50} Leads pro Monat`,
                        'KI-Website-Analyse',
                        'Matching-Score & Priorisierung',
                        'Outreach-Generierung',
                        'Voice-ICP-Erstellung',
                        `+${plan.addon_leads || 100} Leads für ${(plan.addon_price_eur || 29.99).toFixed(2).replace('.', ',')}€`,
                      ].map((f, j) => (
                        <li key={j} className="flex items-center gap-2.5 text-sm text-gray-600">
                          <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Button className={`w-full h-11 ${isPopular ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-600/20' : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-200'}`}
                      onClick={() => navigate('/register')} data-testid={`landing-subscribe-${plan.id}`}>
                      Jetzt registrieren <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section id="faq" className="py-20 sm:py-28 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-12" data-scroll="fade-up">
            <Badge className="mb-4 bg-gray-200 text-gray-700 border-gray-300">FAQ</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">
              Häufig gestellte Fragen
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-scroll="fade-up" data-scroll-delay={i < 4 ? i : 3}
                className="border border-gray-200 rounded-xl px-5 data-[state=open]:border-emerald-300 data-[state=open]:bg-emerald-50/30 transition-colors"
                data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-gray-900 hover:no-underline py-5 text-base font-medium">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-500 leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Beratungs-CTA */}
          <div className="mt-12 p-6 rounded-2xl bg-emerald-50 border border-emerald-200 text-center" data-scroll="fade-up" data-testid="faq-cta-box">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Noch Fragen? Wir beraten Sie gerne!</h3>
            <p className="text-sm text-gray-600 mb-4 max-w-lg mx-auto">
              Erfahren Sie in einem kostenlosen Beratungsgespräch, wie LaizyLeads Ihrem Vertrieb helfen kann.
              Nach Absprache ist auch ein kostenloser Test möglich.
            </p>
            <a href="https://www.laizyones.de" target="_blank" rel="noopener noreferrer" data-testid="faq-booking-btn">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white h-11 px-8 font-semibold">
                Kostenloses Beratungsgespräch buchen <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="py-20 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 to-emerald-700" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center" data-scroll="fade-up">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
            Bereit, Ihre Kundenakquise zu revolutionieren?
          </h2>
          <p className="text-emerald-100 text-base md:text-lg mb-8 max-w-xl mx-auto">
            Registrieren Sie sich jetzt und finden Sie noch heute Ihre ersten qualifizierten B2B-Leads.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" className="bg-white hover:bg-gray-100 text-emerald-700 h-12 px-8 text-base shadow-xl font-semibold"
              onClick={() => navigate('/register')} data-testid="cta-register-btn">
              Jetzt registrieren <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 h-12 px-8 text-base"
              onClick={() => scrollTo('pricing')} data-testid="cta-pricing-btn">
              Preise ansehen
            </Button>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <MarketingFooter />
    </div>
  );
}
